<template>
    <div v-if="!loading" id="location-list">
        <div>
            <span v-if="tripIcons.start" class="icon material-symbols-outlined">
                {{ tripIcons.start }}
            </span>

            <template v-if="!sorting">
                <template v-for="group in groups" :key="groupKey(group)">
                    <div
                        v-if="group.type === 'stay'"
                        class="stay"
                        @mouseenter="hoverItems(stayLocationIds(group))"
                        @mouseleave="clearHover"
                    >
                        <LocationHeadline
                            :location="group.location"
                            v-bind="iconProps(group.location)"
                            @impression-updated="
                                updateImpression($event, group.location.id)
                            "
                        />
                        <div class="stay-range">
                            {{ $formatDate(group.start, true) }} –
                            {{ $formatDate(group.end, true) }}
                        </div>
                        <div
                            v-for="dateGroup in group.dayTrips"
                            :key="dateGroup.date"
                            class="date-group day-trips"
                        >
                            <div
                                class="date"
                                @mouseenter="
                                    hoverItems(itemIds(dateGroup.items))
                                "
                                @mouseleave="hoverItems(stayLocationIds(group))"
                            >
                                <span>{{ $formatDate(dateGroup.date) }}</span>
                            </div>
                            <LocationHeadline
                                v-for="location in dateGroup.items"
                                :key="location.id"
                                :location="location"
                                variant="nested"
                                v-bind="iconProps(location)"
                                @mouseenter="hoverItems([location.id])"
                                @mouseleave="hoverItems(stayLocationIds(group))"
                                @impression-updated="
                                    updateImpression($event, location.id)
                                "
                            />
                        </div>
                    </div>
                    <div
                        v-else
                        class="date-group"
                        :class="[
                            group.type === 'visit' ? 'visits' : 'transit',
                            { 'on-the-way': group.onTheWay },
                        ]"
                        @mouseenter="hoverItems(itemIds(group.items))"
                        @mouseleave="clearHover"
                    >
                        <div class="date">
                            <span>
                                {{ $formatDate(group.date) }}
                                <template v-if="group.onTheWay">
                                    · on the way
                                </template>
                            </span>
                        </div>
                        <LocationHeadline
                            v-for="location in group.items"
                            :key="location.id"
                            :location="location"
                            :variant="
                                group.type === 'visit' ? 'top' : 'transit'
                            "
                            v-bind="iconProps(location)"
                            @mouseenter="hoverItems([location.id])"
                            @mouseleave="hoverItems(itemIds(group.items))"
                            @impression-updated="
                                updateImpression($event, location.id)
                            "
                        />
                    </div>
                </template>
            </template>

            <template v-else>
                <div
                    v-for="dateGroup in sortableGroups"
                    :key="dateGroup.date"
                    class="date-group sortable"
                >
                    <div class="date">
                        <span>{{ $formatDate(dateGroup.date) }}</span>
                    </div>
                    <draggable
                        :list="dateGroup.items"
                        item-key="id"
                        handle=".drag-handle"
                        class="sortable-list"
                        chosen-class="chosen"
                        ghost-class="ghost"
                        @end="emitOrder"
                    >
                        <template #item="{ element }">
                            <div class="location sortable-item">
                                <span
                                    class="drag-handle icon material-symbols-outlined"
                                >
                                    drag_indicator
                                </span>
                                <span
                                    v-if="icons[element.id]?.name"
                                    class="icon material-symbols-outlined"
                                >
                                    {{ icons[element.id].name }}
                                </span>
                                <div class="location-name">
                                    {{ element.name }}
                                </div>
                                <span
                                    v-if="element.nights > 0"
                                    class="nights-badge small"
                                >
                                    <span
                                        class="icon material-symbols-outlined"
                                    >
                                        nights_stay
                                    </span>
                                    {{ element.nights }}
                                </span>
                            </div>
                        </template>
                    </draggable>
                </div>
            </template>

            <span v-if="tripIcons.end" class="icon material-symbols-outlined">
                {{ tripIcons.end }}
            </span>
        </div>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import LocationHeadline from "js/components/list-items/LocationHeadline";
import { useTripStore } from "js/stores/tripStore";

export default {
    name: "LocationList",
    components: {
        LocationHeadline,
        draggable,
    },
    props: {
        trip: {
            type: Object,
            required: true,
        },
        locations: {
            type: Array,
            required: true,
        },
        groups: {
            type: Array,
            required: true,
        },
        icons: {
            type: Object,
            required: true,
        },
        tripIcons: {
            type: Object,
            required: true,
        },
        sorting: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["hover", "update:order", "impression-updated"],
    data() {
        return {
            loading: true,
            sortableGroups: [],
            hoverClearTimeout: null,
        };
    },
    watch: {
        sorting(value) {
            if (value) {
                this.buildSortableGroups();
            }
        },
        locations() {
            if (this.sorting) {
                this.buildSortableGroups();
            }
        },
    },
    async mounted() {
        this.store = useTripStore();
        this.loading = false;
    },
    beforeUnmount() {
        clearTimeout(this.hoverClearTimeout);
    },
    methods: {
        groupKey(group) {
            return group.type === "stay"
                ? `stay-${group.location.id}`
                : `${group.type}-${group.date}-${group.items[0].id}`;
        },
        iconProps(location) {
            const icon = this.icons[location.id];

            return {
                icon: icon?.name ?? null,
                reserveIcon: icon?.reserve ?? false,
            };
        },
        itemIds(items) {
            return items.map((item) => item.id);
        },
        stayLocationIds(group) {
            return [
                group.location.id,
                ...group.dayTrips.flatMap((dateGroup) =>
                    this.itemIds(dateGroup.items)
                ),
            ];
        },
        hoverItems(ids) {
            clearTimeout(this.hoverClearTimeout);
            this.$emit("hover", ids);
        },
        clearHover() {
            clearTimeout(this.hoverClearTimeout);
            this.hoverClearTimeout = setTimeout(() => {
                this.$emit("hover", []);
            }, 100);
        },
        buildSortableGroups() {
            const groups = [];
            let current = null;
            this.locations.forEach((location) => {
                if (!current || current.date !== location.date) {
                    current = { date: location.date, items: [] };
                    groups.push(current);
                }
                current.items.push(location);
            });
            this.sortableGroups = groups;
        },
        emitOrder() {
            const order = this.sortableGroups.flatMap((group) =>
                this.itemIds(group.items)
            );
            this.$emit("update:order", order);
        },
        updateImpression(impression, locationId) {
            this.store
                .edit(`/api/trips/${this.trip.id}/locations/${locationId}/`, {
                    impression,
                })
                .then((data) => {
                    this.store.replaceLocation(this.trip.id, locationId, data);
                    this.$emit("impression-updated");
                });
        },
    },
};
</script>
